import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { BarChart3, Loader2, FolderOpen, AlertTriangle, ClipboardList, CheckCircle2, FileDown, TrendingUp, Calendar, X } from 'lucide-react';
import { toast } from 'sonner';
import ProjectDrilldownModal from '../../components/ProjectDrilldownModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = { open: '#3b82f6', in_progress: '#f59e0b', resolved: '#22c55e', closed: '#94a3b8' };

const Sparkline = ({ points }) => {
  if (!points || points.length === 0) return <span className="text-xs text-slate-300">—</span>;
  const w = 120, h = 32, max = Math.max(...points.map((p) => p.cumulative)) || 1;
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${(h - (p.cumulative / max) * h).toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} className="overflow-visible">
      <path d={path} fill="none" stroke="#4f46e5" strokeWidth="2" />
      <circle cx={((points.length - 1) * step).toFixed(1)} cy={(h - (points[points.length - 1].cumulative / max) * h).toFixed(1)} r="2.5" fill="#4f46e5" />
    </svg>
  );
};

const Kpi = ({ icon: Icon, label, value, tone }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid={`kpi-${label.toLowerCase().replace(/ /g, '-')}`}>
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${tone}`}><Icon className="w-5 h-5" /></div>
    <p className="text-3xl font-bold text-slate-900">{value}</p>
    <p className="text-sm text-slate-500">{label}</p>
  </div>
);

const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [drilldown, setDrilldown] = useState(null);

  const rangeQuery = () => {
    const p = [];
    if (start) p.push(`start=${start}`);
    if (end) p.push(`end=${end}`);
    return p.length ? `?${p.join('&')}` : '';
  };

  const fetchData = () => {
    setLoading(true);
    axios.get(`${API}/analytics/dashboard${rangeQuery()}`).then((r) => setData(r.data)).catch(() => toast.error('Failed to load analytics')).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [start, end]);

  const exportAs = async (format) => {
    setExporting(format);
    try {
      const sep = rangeQuery() ? '&' : '?';
      const res = await axios.get(`${API}/analytics/dashboard/export${rangeQuery()}${sep}format=${format}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  if (loading && !data) {
    return <Layout><div className="flex items-center justify-center h-64 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading analytics…</div></Layout>;
  }

  const t = data?.totals || {};
  const trend = data?.completionTrend || [];
  const maxTrend = Math.max(1, ...trend.map((x) => x.count));
  const issuesByStatus = data?.issuesByStatus || {};
  const perProject = data?.perProject || [];
  const zones = data?.zoneHeatmap || [];
  const maxFlag = Math.max(1, ...perProject.map((p) => p.flaggedDeviations));

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><BarChart3 className="w-7 h-7 text-indigo-500" /> Portfolio Analytics</h1>
            <p className="text-slate-600 mt-1">Across all projects · deviation tolerance {data?.tolerance}%</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap" data-testid="analytics-date-filter">
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1">
              <Calendar className="w-4 h-4 text-slate-400" />
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="text-sm outline-none" data-testid="analytics-start-date" />
              <span className="text-slate-300">→</span>
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="text-sm outline-none" data-testid="analytics-end-date" />
              {(start || end) && (
                <button onClick={() => { setStart(''); setEnd(''); }} className="text-slate-400 hover:text-slate-600 ml-1" data-testid="analytics-clear-date"><X className="w-4 h-4" /></button>
              )}
            </div>
            <button onClick={() => exportAs('pdf')} disabled={exporting} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-60" data-testid="export-pdf-btn">
              {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} PDF
            </button>
            <button onClick={() => exportAs('csv')} disabled={exporting} className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 flex items-center gap-2 disabled:opacity-60" data-testid="export-csv-btn">
              {exporting === 'csv' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} CSV
            </button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-kpis">
          <Kpi icon={FolderOpen} label="Projects" value={t.projects || 0} tone="bg-blue-100 text-blue-600" />
          <Kpi icon={CheckCircle2} label="Completed Jobs" value={t.completedJobs || 0} tone="bg-green-100 text-green-600" />
          <Kpi icon={AlertTriangle} label="Flagged Deviations" value={t.flaggedDeviations || 0} tone="bg-red-100 text-red-600" />
          <Kpi icon={ClipboardList} label="Open Issues" value={t.openIssues || 0} tone="bg-amber-100 text-amber-600" />
        </div>

        {perProject.length === 0 || (t.jobs === 0) ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400" data-testid="analytics-empty">
            {(start || end)
              ? 'No jobs or issues in the selected date range. Adjust or clear the filter to see data.'
              : 'No projects yet. Analytics will populate as you add projects and run analyses.'}
          </div>
        ) : (
          <>
            {/* Completion trend + issues by status */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm lg:col-span-2" data-testid="completion-trend">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-indigo-500" /> Completion Trend</h3>
                {trend.length === 0 ? <p className="text-sm text-slate-400">No completed jobs yet.</p> : (
                  <div className="flex items-end gap-1 h-40">
                    {trend.map((x) => (
                      <div key={x.date} className="flex-1 flex flex-col items-center justify-end group" title={`${x.date}: ${x.count}`}>
                        <div className="w-full bg-indigo-500 rounded-t hover:bg-indigo-600 transition-colors" style={{ height: `${(x.count / maxTrend) * 100}%` }} />
                        <span className="text-[9px] text-slate-400 mt-1 rotate-0 truncate w-full text-center">{x.date.slice(5)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="issues-by-status">
                <h3 className="font-semibold text-slate-800 mb-4">Issues by Status</h3>
                <div className="space-y-3">
                  {Object.keys(issuesByStatus).length === 0 && <p className="text-sm text-slate-400">No issues.</p>}
                  {Object.entries(issuesByStatus).map(([s, n]) => {
                    const total = Object.values(issuesByStatus).reduce((a, b) => a + b, 0) || 1;
                    return (
                      <div key={s}>
                        <div className="flex justify-between text-xs mb-1"><span className="capitalize text-slate-600">{s.replace('_', ' ')}</span><span className="font-semibold">{n}</span></div>
                        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${(n / total) * 100}%`, backgroundColor: STATUS_COLORS[s] || '#64748b' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Per-project table with S-curve */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="per-project-table">
              <h3 className="font-semibold text-slate-800 p-5 pb-3">Per-Project Progress</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-100">
                      <th className="px-5 py-2 font-medium">Project</th>
                      <th className="px-3 py-2 font-medium">Progress (S-curve)</th>
                      <th className="px-3 py-2 font-medium text-center">Completed</th>
                      <th className="px-3 py-2 font-medium text-center">Flagged</th>
                      <th className="px-3 py-2 font-medium text-center">Open Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perProject.map((p) => (
                      <tr key={p.projectId} className="border-b border-slate-50 hover:bg-indigo-50 cursor-pointer" data-testid="project-row" onClick={() => setDrilldown(p)}>
                        <td className="px-5 py-3 font-medium text-indigo-700 hover:underline">{p.projectName}</td>
                        <td className="px-3 py-3"><Sparkline points={p.sCurve} /></td>
                        <td className="px-3 py-3 text-center">{p.completedJobs}/{p.totalJobs}</td>
                        <td className="px-3 py-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${p.flaggedDeviations > 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`}
                            style={p.flaggedDeviations > 0 ? { backgroundColor: `rgba(239,68,68,${0.15 + 0.6 * (p.flaggedDeviations / maxFlag)})`, color: '#7f1d1d' } : {}}>
                            {p.flaggedDeviations}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">{p.openIssues}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Zone heatmap */}
            {zones.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="zone-heatmap">
                <h3 className="font-semibold text-slate-800 mb-3">Deviation Hotspots by Drawing Zone</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {zones.map((z) => {
                    const maxZone = Math.max(1, ...zones.map((x) => x.flagged));
                    return (
                      <div key={`${z.projectId}-${z.drawingId}`} className="rounded-lg p-3 border border-slate-100" style={{ backgroundColor: `rgba(239,68,68,${0.1 + 0.7 * (z.flagged / maxZone)})` }} data-testid="zone-tile">
                        <p className="text-xs font-medium text-slate-700 truncate">{z.projectName}</p>
                        <p className="text-[10px] text-slate-500 font-mono truncate">plan {z.drawingId.slice(0, 8)}</p>
                        <p className="text-xl font-bold text-red-800">{z.flagged}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ProjectDrilldownModal project={drilldown} open={!!drilldown} onClose={() => setDrilldown(null)} />
    </Layout>
  );
};

export default AdminAnalytics;
